// =============================================================================
// supplier-history-client.http.ts — Adaptateur HTTP RÉEL du SupplierHistoryClient (Lot A, DBU §3.9).
// =============================================================================
// Câble la SPA contre les endpoints FOURNISSEUR `GET /supplier/history/participations` et
// `GET /supplier/history/participations/:campaignId`. Substitué derrière le MÊME DI seam
// (SupplierHistoryClientProvider). LECTURE PURE : aucune mutation, donc PAS de CSRF.
//
// SÉCURITÉ (RULE_REF_006) : la session fournisseur (cookie httpOnly `supplier_session`) n'est JAMAIS lue
// ni fabriquée ici — portée par le cookie (credentials: 'include'). Le siret vient de la session côté API.
// AUCUNE donnée métier en localStorage. MAPPING ERREURS par STATUS HTTP (401/403/404), message relayé.

import {
  SupplierHistoryApiError,
  type SupplierHistoryApiErrorKind,
  type SupplierHistoryClient,
  type HistoryDetailDto,
  type SupplierHistoryDto,
} from '@/api/supplier-history-client';

/** Forme du corps d'erreur NestJS (HttpException standard) — message éventuellement string[]. */
interface NestErrorBody {
  readonly statusCode?: number;
  readonly message?: string | readonly string[];
  readonly error?: string;
}

/** Mapping STATUS HTTP → kind UX (lecture : 401 session, 403 garde, 404 ressource). */
const STATUS_TO_KIND: Readonly<Record<number, SupplierHistoryApiErrorKind>> = {
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not-found',
};

/**
 * Adaptateur HTTP réel : appels `fetch` contre l'API historique fournisseur. `baseUrl` par défaut vide
 * (chemins relatifs : SPA et API partagent l'origine, ou un reverse proxy route `/supplier/*`).
 */
export class HttpSupplierHistoryClient implements SupplierHistoryClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public async listParticipations(): Promise<SupplierHistoryDto> {
    const response = await fetch(
      `${this.baseUrl}/supplier/history/participations`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
      },
    );
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as SupplierHistoryDto;
  }

  public async getDetail(campaignId: string): Promise<HistoryDetailDto> {
    const response = await fetch(
      `${this.baseUrl}/supplier/history/participations/${encodeURIComponent(campaignId)}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
      },
    );
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as HistoryDetailDto;
  }

  private async toApiError(response: Response): Promise<SupplierHistoryApiError> {
    const body = await this.safeJson(response);
    const message =
      this.extractMessage(body) ?? `Erreur API (${String(response.status)}).`;
    const kind = STATUS_TO_KIND[response.status] ?? 'generic';
    return new SupplierHistoryApiError(message, kind);
  }

  private extractMessage(body: NestErrorBody | undefined): string | undefined {
    const message = body?.message;
    if (message === undefined) {
      return undefined;
    }
    return typeof message === 'string' ? message : message.join(' ');
  }

  private async safeJson(response: Response): Promise<NestErrorBody | undefined> {
    try {
      return (await response.json()) as NestErrorBody;
    } catch {
      return undefined;
    }
  }
}
